import Container from "react-bootstrap/Container"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"
import Button from "react-bootstrap/Button"
import "../styles/ArticlePage.css"
import { useEffect, useState } from "react"
import ArticleForm from "./ArticleForm"
import { useNavigate } from "react-router-dom"
import Alert from 'react-bootstrap/Alert'

import { useTranslation } from 'react-i18next'
import eventService from "../services/eventService"
import newsService from "../services/newsService"
import { BASE_SERVER_URL } from "../constants"
import windowUtils from "../utils/windowUtils"
import BreikkiAlert from "./BreikkiAlert"
import LoadingAnimation from "./LoadingAnimation"

/**
 * Call this in a component that mounts on every page/route change
 * to reliably scroll the window to top.
 */
const useScrollToTop = () => {
  useEffect(() => {
    // Disable browser auto-restoration of scroll
    // eslint-disable-next-line no-restricted-globals
    if ("scrollRestoration" in history) {
        // eslint-disable-next-line no-restricted-globals
      history.scrollRestoration = "manual"
    }

    // Scroll to top using requestAnimationFrame
    const scrollToTop = () => window.scrollTo(0, 0)

    // First frame
    requestAnimationFrame(scrollToTop)

    // Extra insurance for stubborn browsers
    const timeoutId = setTimeout(scrollToTop, 0)

    return () => clearTimeout(timeoutId)
  }, [])
}


const ArticlePage = ({ eventId, newsId, isAdmin }) => {
    const { t } = useTranslation()
    const [error, setError] = useState(null)
    const [editMode, setEditMode] = useState(null)
    const [event, setEvent] = useState(null)
    const [news, setNews] = useState(null)
    const [article, setArticle] = useState(null)
    const [loading, setLoading] = useState(null)
    const navigate = useNavigate()
    const articleType = eventId ? "event" : newsId ? "news" : "plain"
    useScrollToTop()
    const loadingWidth = window.innerWidth < 768 ? 200 : 400;

    useEffect(() => {
        setLoading(true)
        let retPromise
        switch (articleType) {
            case "event":
                retPromise = eventService.getEvent(eventId)
                break
            case "news":
                retPromise = newsService.getNews(newsId)
                break
            default:
                return
        }

        retPromise
            .then((res) => {
                if (!res) {
                    console.error("error fetching article", res)
                    setLoading(false)
                    setError(true)
                    return
                }
                setArticle(res.article)
                setLoading(false)
                setError(false)
                if (articleType === "event") setEvent(res)
                if (articleType === "news") setNews(res)
            })
            .catch((err) => {
                console.error(err)
                setError(true)
            })
    }, [articleType, eventId, newsId])


    const getArticleDateString = () => {
        if (!article) {
            console.warn("getArticleDateString: No article data available.")
            return ""
        }

        return getDateString(article.modifiedDate ? article.modifiedDate : article.createdDate)
    }

    const getDateString = (dateString) => {
        const d = new Date(dateString)

        // Check if the Date object is valid
        if (isNaN(d.getTime())) {
            console.error("getDateString: Invalid Date object created.")
            return ""
        }

        // Format both date and time in Finnish locale
        const options = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit"
        }

        const formattedDateTime = d.toLocaleString("fi-FI", options)
        console.debug("getDateString: Formatted date and time ->", formattedDateTime)

        return formattedDateTime
    }



    const deletePage = () => {
        // Add a confirmation prompt before proceeding
        const isConfirmed = window.confirm(t("article_page_delete_article_confirmation"))

        if (!isConfirmed) {
            // If the user cancels, exit the function
            return
        }
        let retPromise
        switch (articleType) {
            case "event":
                retPromise = eventService.deleteEvent(event._id)
                break
            case "news":
                retPromise = newsService.deleteNews(news._id)
                break
            // case "plain":
            //     retPromise = articleService.deleteArticle(article._id)
            //     break
        }

        retPromise.then((ret) => {
            if (ret.response && ret.response.status !== 200) {
                setError("Failed to delete page. Status:" + ret.response.status)
            } else {
                alert(t("article_page_deleted_alert_message"))
                navigate("/")
                setTimeout(() => {
                    window.location.reload()
                }, 100)
            }
        }).catch(err => {
            console.error(err)
            setError("Cannot delete page.")
        })
    }


    return (
        <div>
            <Container className="BREIKKI-article-page-container">
                {loading &&
                    <Container className="BREIKKI-article-page-loading-container" style={{ maxWidth: loadingWidth }}>
                        <LoadingAnimation width={loadingWidth} />
                    </Container>
                }
                {error ?
                    <BreikkiAlert isError={true} onRetry={() => windowUtils.reloadWindow()} message={t("article_page_fetch_error_generic")}  />
                    :
                    editMode ? (
                        <Container>
                            <Row>
                                <Col>
                                    <h2 className="BREIKKI-article-page-title">{t("article_page_editing_article_title")}</h2>
                                </Col>
                                {
                                    isAdmin && (
                                        <Col className="d-flex align-items-end">
                                            <Button className={"BREIKKI-article-page-edit-button"} onClick={() => setEditMode(!editMode)}>{t("article_form_stop_editing_article")}</Button>
                                        </Col>
                                    )
                                }
                            </Row>
                            {error && (
                                <Row>
                                    <Alert variant="danger">
                                        {error}
                                    </Alert>
                                </Row>
                            )}
                            <ArticleForm article={article} event={event} news={news} editMode={true} setEditMode={setEditMode} />
                            <Button variant="danger" className={"BREIKKI-article-page-edit-button BREIKKI-article-page-delete-button"} onClick={deletePage}>{t("article_form_delete_article")}</Button>
                        </Container>
                    ) : (
                        article &&
                        <>
                            {event && event.fileUrl &&
                                <Row className="BREIKKI-article-page-event-img-container">
                                    <img className="BREIKKI-article-page-event-img"
                                        src={`${BASE_SERVER_URL}${event.fileUrl}`} />
                                </Row>
                            }
                            <div className="BREIKKI-article-page-article">
                                <Row>
                                    <div className="d-flex justify-content-between">
                                        <h2 className="BREIKKI-article-page-title">{article.title}</h2>
                                        {isAdmin && (
                                            <Button className={"BREIKKI-article-page-edit-button"} onClick={() => setEditMode(!editMode)}>{t("article_form_edit_article_title")}</Button>
                                        )
                                        }
                                    </div>
                                    {!!event && event.organizedOn && <p className="BREIKKI-article-page-event-organizedOn">{getDateString(event.organizedOn)} @ <strong>{event.venue}</strong></p>}
                                </Row>
                                <Row>
                                    <Col>
                                        <div className="BREIKKI-pre BREIKKI-article-page-fulltext" dangerouslySetInnerHTML={{ __html: article.fullText }}></div>
                                    </Col>
                                </Row>
                                <Row>
                                    <span className="BREIKKI-article-page-date-time">
                                        {`${t("article_page_date_time_updated_on")}: ${getArticleDateString()}`}
                                    </span>
                                </Row>
                            </div>
                        </>
                    )
                }
            </Container>
        </div>
    )
}

export default ArticlePage